import React from 'react';
import { classes } from 'typestyle';

import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { FavoriteToggleClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { Messages } from '~src/codegen/Localize';
import { ScreenReaderService } from '~src/commonInfra/services/ScreenReaderService';
import { FavoriteToggleClickedEventHandler, FavoriteToggleType } from '~src/commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { PageHeaderButtonType } from '~src/spacesPlatform/components/layout/PageHeader';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';

export type FavoriteToggleProps = {
  isFavorite: boolean;
  onAddFavorite: () => void;
  onRemoveFavorite: () => void;
  isTabbable: boolean;
  itemName: string | undefined;
  hideHelpTooltip?: boolean;
  buttonType?: PageHeaderButtonType;
};

const telemetryConsumer = FavoriteToggleClickedTelemetryContext.Consumer;

export const FavoriteToggle = (props: FavoriteToggleProps) => {
  const { isFavorite, onAddFavorite, onRemoveFavorite, isTabbable, itemName, hideHelpTooltip, buttonType, ...domAttrs } = props;
  const [isToggled, setToggled] = React.useState<boolean>(isFavorite);

  const buttonClasses = classes(
    'tb-favorite-button',
    isToggled && 'tb-selected',
    PageHeaderStyles.FavoriteButtonFocusClass,
  );

  const label = Messages.favoriteToggleButtonLabel();
  const title = hideHelpTooltip ? undefined : label;
  const toggleType = isToggled ? FavoriteToggleType.Unfavorite : FavoriteToggleType.Favorite;

  const toggle = () => {
    const callback = isToggled ? onRemoveFavorite : onAddFavorite;
    setToggled((state: boolean) => !state);
    callback();
    if (itemName) {
      const announcement = isToggled
        ? Messages.favoriteRemoved({ NAME: itemName })
        : Messages.favoriteAdded({ NAME: itemName });
      ScreenReaderService.instance.setScreenReaderMessage(announcement);
    }
  };

  const handleClick = (e: React.SyntheticEvent<HTMLSpanElement>) => {
    toggle();
    e.stopPropagation();
    e.preventDefault();
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      handleClick(e);
    }
  };

  React.useEffect(() => {
    setToggled(isFavorite);
  }, [isFavorite]);

  return telemetryConsumerWrapper(telemetryConsumer, (sendTelemetry: FavoriteToggleClickedEventHandler) => {
    return (
      <span
        className={buttonClasses}
        tabIndex={isTabbable ? 0 : -1}
        onClick={(e: React.SyntheticEvent<HTMLSpanElement>) => {
          sendTelemetry(toggleType);
          handleClick(e);
        }}
        onKeyDown={handleKeypress}
        title={title}
        role={'button'}
        aria-label={label}
        aria-pressed={isToggled}
        {...domAttrs}
      />
    );
  });
};
